import { Component, type ReactNode } from 'react';
import LoadingState from './ui/LoadingState';
import { CHUNK_ERROR_PATTERN, alreadyReloadedForStaleChunk, reloadForStaleChunk } from '../lib/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  reloading: boolean;
}

// Catches the render-time throw when a lazy route's dynamic import()
// rejects (see lib/chunkReload.ts for why that happens) — without this,
// React just unmounts the tree on that error, leaving a blank page.
export default class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reloading: false };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (CHUNK_ERROR_PATTERN.test(error.message) && !alreadyReloadedForStaleChunk()) {
      this.setState({ reloading: true });
      reloadForStaleChunk();
    }
  }

  render() {
    if (this.state.reloading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <LoadingState label="The site was just updated — refreshing…" />
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-8 text-center">
          <div>
            <h1 className="font-serif font-medium text-[26px] text-ink m-0 mb-3">Something went wrong</h1>
            <p className="text-[15px] text-ink-soft max-w-[420px] mx-auto mb-6">
              This page couldn't load. Please refresh to try again.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-olive text-paper text-[14px] font-semibold border-none cursor-pointer transition-colors hover:bg-olive-dark"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
